import { useCallback, useMemo, useRef, useState } from "react";
import {
  Form,
  Input,
  Button,
  Typography,
  Row,
  Col,
  message,
  Alert,
  Select,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  ProjectOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";

import { REGISTRATION_SUCCESS_MESSAGE } from "./registration.constants";
import { useRegisterMutation, useSecurityQuestionsQuery } from "./registration.hooks";
import { getRoleFromToken, getTokenType, normalizeRegisterToken } from "./registration.utils";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import UnsavedChangesModal from "../../components/UnsavedChangesModal/UnsavedChangesModal";
import "../../styles/trackon.css"

const { Title, Text } = Typography;

/**
 * Reusable registration form.
 *
 * It can be used in:
 * 1. Normal registration page
 * 2. Admin "Add User" dialog
 *
 * When embedded=true:
 * - It does NOT store the newly-created user's token
 * - It does NOT call onLogin
 * - It does NOT navigate
 * - It calls onSuccess after successful registration
 */
export function RegistrationForm({
  embedded = false,
  onSuccess,
  onCancel,
}) {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const isFormDirtyRef = useRef(false);
  const navigationBlocker = useUnsavedChangesGuard(isFormDirtyRef);
  const [error, setError] = useState("");

  const registerMutation = useRegisterMutation();
  const securityQuestionsQuery = useSecurityQuestionsQuery();

  const securityQuestions = useMemo(
    () => securityQuestionsQuery.data ?? [],
    [securityQuestionsQuery.data],
  );

  const questionsLoading =
    securityQuestionsQuery.isFetching &&
    !securityQuestionsQuery.data;

  const questionOptions = useMemo(
    () =>
      securityQuestions.map((q) => ({
        value: q.value,
        label: q.label,
      })),
    [securityQuestions],
  );

  const handleSubmit = useCallback(async () => {
    try {
      await form.validateFields();
      setError("");
      setLoading(true);

      const values = form.getFieldsValue();
      const data = await registerMutation.mutateAsync(values);

      message.success(
        data.message || REGISTRATION_SUCCESS_MESSAGE,
      );

      /*
       * Notify the parent (Register wrapper or Admin page)
       * that registration succeeded.
       *
       * - Normal registration flow: the wrapper stores the
       *   token, calls onLogin and navigates.
       * - Embedded/admin flow: the admin is creating another
       *   user's account, so no token is stored here.
       */
      isFormDirtyRef.current = false;

      if (onSuccess) {
        await onSuccess(data, values);
      }

      if (embedded) {
        /*
         * Clear the form after successful creation.
         */
        form.resetFields();
      }
    } catch (e) {
      const errorMsg =
        e instanceof Error
          ? e.message
          : "Registration failed";

      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [
    embedded,
    form,
    onSuccess,
    registerMutation,
  ]);

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={handleSubmit}
      onValuesChange={() => {
        isFormDirtyRef.current = true;
      }}
    >
      <UnsavedChangesModal blocker={navigationBlocker} />
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{
            marginBottom: 16,
            borderRadius: 8,
          }}
        />
      )}

      <Form.Item
        label={
          <Text style={{ fontWeight: 600 }}>
            UST UID
          </Text>
        }
        name="uid"
        rules={[
          {
            required: true,
            message: "UST UID is required",
          },
          {
            len: 6,
            message:
              "UST UID must be exactly 6 characters.",
          },
        ]}
      >
        <Input
          size="large"
          placeholder="Enter your UST UID"
          style={{ borderRadius: 10 }}
        />
      </Form.Item>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            label={
              <Text style={{ fontWeight: 600 }}>
                First Name
              </Text>
            }
            name="firstName"
            rules={[
              {
                required: true,
                message: "Required",
              },
              {
                pattern:
                  /^[A-Za-z]+(?:\s+[A-Za-z]+)*$/,
                message:
                  "First name must contain letters and spaces only",
              },
            ]}
          >
            <Input
              prefix={
                <UserOutlined
                  style={{ color: "#bfbfbf" }}
                />
              }
              placeholder="Enter your First Name"
              size="large"
              style={{ borderRadius: 10 }}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label={
              <Text style={{ fontWeight: 600 }}>
                Last Name
              </Text>
            }
            name="lastName"
            rules={[
              {
                required: true,
                message: "Required",
              },
              {
                pattern:
                  /^[A-Za-z]+(?:\s+[A-Za-z]+)*$/,
                message:
                  "Last name must contain letters and spaces only",
              },
            ]}
          >
            <Input
              placeholder="Enter your Last Name"
              size="large"
              style={{ borderRadius: 10 }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label={
          <Text style={{ fontWeight: 600 }}>
            Security Question
          </Text>
        }
        name="questionId"
        rules={[
          {
            required: true,
            message:
              "Please select a security question",
          },
        ]}
      >
        <Select
          placeholder="Select a security question"
          size="large"
          loading={questionsLoading}
          notFoundContent={
            questionsLoading
              ? "Loading questions..."
              : "No questions available"
          }
          style={{ borderRadius: 10 }}
          options={questionOptions}
          /*
           * When this form is embedded inside a MUI Dialog
           * (Admin > Add User), the antd dropdown must render
           * above the dialog (MUI z-index is 1300).
           */
          styles={{ popup: { root: { zIndex: 2000 } } }}
          dropdownStyle={{ zIndex: 2000 }}
        />
      </Form.Item>

      <Form.Item
        label={
          <Text style={{ fontWeight: 600 }}>
            Security Answer
          </Text>
        }
        name="securityAnswer"
        rules={[
          {
            required: true,
            message: "Security answer is required",
          },
          {
            min: 2,
            message:
              "Answer must be at least 2 characters",
          },
        ]}
      >
        <Input.Password
          placeholder="Enter your security answer"
          size="large"
          style={{ borderRadius: 10 }}
        />
      </Form.Item>

      <Form.Item
        label={
          <Text style={{ fontWeight: 600 }}>
            Password
          </Text>
        }
        name="password"
        rules={[
          {
            required: true,
            message: "Password is required",
          },
          {
            min: 8,
            message: "At least 8 characters",
          },
          {
            pattern: /^(?=.*[A-Z])(?=.*\d)/,
            message:
              "Must contain an uppercase letter and a number",
          },
        ]}
      >
        <Input.Password
          prefix={
            <LockOutlined
              style={{ color: "#bfbfbf" }}
            />
          }
          placeholder="Create a strong password"
          size="large"
          style={{ borderRadius: 10 }}
          iconRender={(visible) =>
            visible ? (
              <EyeTwoTone />
            ) : (
              <EyeInvisibleOutlined />
            )
          }
          autoComplete="new-password"
        />
      </Form.Item>

      <Form.Item
        label={
          <Text style={{ fontWeight: 600 }}>
            Confirm Password
          </Text>
        }
        name="confirmPassword"
        dependencies={["password"]}
        rules={[
          {
            required: true,
            message:
              "Please confirm your password",
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (
                !value ||
                getFieldValue("password") === value
              ) {
                return Promise.resolve();
              }

              return Promise.reject(
                new Error("Passwords do not match"),
              );
            },
          }),
        ]}
      >
        <Input.Password
          prefix={
            <LockOutlined
              style={{ color: "#bfbfbf" }}
            />
          }
          placeholder="Confirm Password"
          size="large"
          style={{ borderRadius: 10 }}
          iconRender={(visible) =>
            visible ? (
              <EyeTwoTone />
            ) : (
              <EyeInvisibleOutlined />
            )
          }
          autoComplete="new-password"
        />
      </Form.Item>

      {embedded ? (
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 20,
          }}
        >
          <Button
            onClick={onCancel}
            disabled={loading}
            size="large"
            style={{
              borderRadius: 10,
              minWidth: 100,
            }}
          >
            Cancel
          </Button>

          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="large"
            style={{
              borderRadius: 10,
              minWidth: 140,
              fontWeight: 600,
            }}
          >
            Create User
          </Button>
        </div>
      ) : (
        <Button
          type="primary"
          htmlType="submit"
          block
          size="large"
          loading={loading}
          style={{
            borderRadius: 10,
            height: 48,
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          Create Account
        </Button>
      )}
    </Form>
  );
}

/**
 * Existing full-page registration component.
 *
 * This preserves the existing registration behaviour.
 */
export function Register({ onLogin }) {
  const navigate = useNavigate();

  const handleRegistrationSuccess = useCallback(
    async (data, values) => {
      /*
       * For the normal registration page we need to
       * perform the existing login/token flow.
       */
      if (data.token) {
        sessionStorage.setItem(
          "token",
          normalizeRegisterToken(data.token),
        );

        sessionStorage.setItem(
          "tokenType",
          data.tokenType || getTokenType(),
        );

        sessionStorage.setItem(
          "tokenExpiryInSeconds",
          String(
            Math.floor(Date.now() / 1000) +
              (data.expiresIn || 0),
          ),
        );

        const userRole = getRoleFromToken(data.token);

        onLogin({
          name: `${values.firstName} ${values.lastName}`,
          email: values.uid,
          role: userRole,
        });
      }

      navigate(
        data.token
          ? "/watchtower/dashboard"
          : "/watchtower",
      );
    },
    [navigate, onLogin],
  );

  return (
    <div
      className="trackon-root"
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #001529 0%, #003a70 50%, #0958d9 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle at 80% 20%, rgba(22,119,255,0.15) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(9,88,217,0.2) 0%, transparent 50%)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          width: "100%",
          maxWidth: 500,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: 28,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              background:
                "linear-gradient(135deg, #1677ff, #0958d9)",
              borderRadius: 14,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 12,
              boxShadow:
                "0 8px 32px rgba(22,119,255,0.4)",
            }}
          >
            <ProjectOutlined
              style={{
                fontSize: 28,
                color: "white",
              }}
            />
          </div>

          <Title
            level={2}
            style={{
              color: "white",
              margin: 0,
              fontWeight: 700,
            }}
          >
            Watch Tower
          </Title>
        </div>

        <div
          style={{
            background: "rgba(255,255,255,0.97)",
            borderRadius: 20,
            padding: "36px 40px",
            boxShadow:
              "0 24px 64px rgba(0,0,0,0.35)",
          }}
        >
          <div
            style={{
              textAlign: "center",
              marginBottom: 32,
            }}
          >
            <Title
              level={2}
              style={{
                marginBottom: 8,
                color: "#1677ff",
                fontWeight: 700,
              }}
            >
              Sign Up
            </Title>

            <Typography.Text
              type="secondary"
              style={{ fontSize: 14 }}
            >
              Create your account to get started
            </Typography.Text>
          </div>

          <RegistrationForm
            embedded={false}
            onSuccess={handleRegistrationSuccess}
          />

          <div
            style={{
              textAlign: "center",
              marginTop: 24,
            }}
          >
            <Text style={{ color: "#8c8c8c" }}>
              Already have an account?{" "}
            </Text>

            <Link to="/watchtower">
              <Text
                style={{
                  color: "#1677ff",
                  fontWeight: 600,
                }}
              >
                Sign in
              </Text>
            </Link>
          </div>
        </div>

        <Text
          style={{
            display: "block",
            textAlign: "center",
            marginTop: 24,
            color: "rgba(255,255,255,0.4)",
            fontSize: 12,
          }}
        >
          © 2026 Watch Tower PMO Suite · v2.1
        </Text>
      </div>
    </div>
  );
}